import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  ArrowUpRight,
  CarFront,
  Check,
  Clock3,
  Copy,
  ExternalLink,
  Footprints,
  Gamepad2,
  Gift,
  Mail,
  MapPin,
  Menu,
  PartyPopper,
  Phone,
  Route,
  Send,
  Sparkles,
  Ticket,
  TrainFront,
  Trophy,
  UsersRound,
  X,
  Zap,
} from 'lucide-react';
import { EXPERIENCES, getPricingForDay, HOURS, PRICE_DAYS, REWARD_DRAFT, TICKET_GAMES, VENUE } from './venueData';

const ROUTE_MODES = [
  { id: 'driving', label: 'Coche', detail: 'Google Maps · conducir', Icon: CarFront },
  { id: 'walking', label: 'A pie', detail: 'Google Maps · caminar', Icon: Footprints },
  { id: 'transit', label: 'Transporte', detail: 'Google Maps · transporte público', Icon: TrainFront },
];

const INITIAL_BOOKING = {
  name: '',
  contact: '',
  date: '',
  groupSize: '4',
  experience: 'Bolos',
  notes: '',
};

function scrollToSection(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function getDirectionsUrl(mode = 'driving') {
  const destination = encodeURIComponent(`${VENUE.name}, ${VENUE.address}, ${VENUE.postalCode}, Alcorcón, Madrid`);
  return `https://www.google.com/maps/dir/?api=1&destination=${destination}&travelmode=${mode}`;
}

function formatBookingMailto(booking) {
  const subject = `Solicitud de reserva · ${booking.experience || 'plan'} · ${booking.date || 'fecha por concretar'}`;
  const body = [
    `Hola, quiero pedir disponibilidad en ${VENUE.name}.`,
    '',
    `Nombre: ${booking.name}`,
    `Contacto: ${booking.contact}`,
    `Fecha preferida: ${booking.date || 'Por concretar'}`,
    `Personas: ${booking.groupSize}`,
    `Experiencia: ${booking.experience}`,
    `Notas: ${booking.notes || 'Sin notas'}`,
  ].join('\n');
  return `mailto:${VENUE.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function BrandMark() {
  return (
    <span className="brand-mark" aria-hidden="true">
      <span className="brand-mark__pin brand-mark__pin--one" />
      <span className="brand-mark__pin brand-mark__pin--two" />
      <span className="brand-mark__ball" />
    </span>
  );
}

function Brand({ footer = false }) {
  return (
    <a className={`brand ${footer ? 'brand--footer' : ''}`} href="#inicio" aria-label="Bowling Tres Aguas · volver al inicio">
      <BrandMark />
      <span className="brand__copy">
        <strong>Bowling</strong>
        <strong>Tres Aguas</strong>
        <small>Alcorcón · Madrid</small>
      </span>
    </a>
  );
}

function ShaderCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const gl = canvas?.getContext('webgl', { alpha: true, antialias: false, powerPreference: 'low-power' });
    if (!gl) return undefined;

    const vertexShaderSource = `
      attribute vec2 a_position;
      void main() { gl_Position = vec4(a_position, 0.0, 1.0); }
    `;
    const fragmentShaderSource = `
      precision highp float;
      uniform vec2 u_resolution;
      uniform float u_time;
      uniform vec2 u_pointer;

      float blob(vec2 uv, vec2 center, float radius) {
        float d = distance(uv, center);
        return 1.0 - smoothstep(0.0, radius, d);
      }

      void main() {
        vec2 uv = gl_FragCoord.xy / u_resolution.xy;
        float aspect = u_resolution.x / max(u_resolution.y, 1.0);
        vec2 centered = uv - vec2(0.5);
        centered.x *= aspect;
        float time = u_time * 0.09;
        vec2 pointer = (u_pointer - 0.5) * vec2(aspect, 1.0) * 0.18;
        float cyan = blob(centered, vec2(-0.3 + sin(time) * 0.11, 0.15 + cos(time * 1.2) * 0.18) + pointer, 0.62);
        float coral = blob(centered, vec2(0.42 + cos(time * 0.8) * 0.14, -0.14 + sin(time * 1.1) * 0.16), 0.58);
        float lime = blob(centered, vec2(-0.02 + sin(time * 1.4) * 0.15, -0.4 + cos(time) * 0.1), 0.38);
        float vignette = smoothstep(1.12, 0.18, length(centered));
        vec3 color = vec3(0.018, 0.024, 0.055);
        color += vec3(0.02, 0.36, 0.49) * cyan * 0.18;
        color += vec3(0.52, 0.08, 0.18) * coral * 0.13;
        color += vec3(0.58, 0.66, 0.08) * lime * 0.07;
        color *= 0.46 + vignette * 0.54;
        gl_FragColor = vec4(color, 0.92);
      }
    `;

    function createShader(type, source) {
      const shader = gl.createShader(type);
      if (!shader) return null;
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    }

    const vertexShader = createShader(gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = createShader(gl.FRAGMENT_SHADER, fragmentShaderSource);
    if (!vertexShader || !fragmentShader) return undefined;
    const program = gl.createProgram();
    if (!program) return undefined;
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return undefined;

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, 'a_position');
    const timeUniform = gl.getUniformLocation(program, 'u_time');
    const resolutionUniform = gl.getUniformLocation(program, 'u_resolution');
    const pointerUniform = gl.getUniformLocation(program, 'u_pointer');
    const pointer = { x: 0.52, y: 0.45 };
    let animationFrame = 0;

    function resize() {
      const ratio = Math.min(window.devicePixelRatio || 1, 1.6);
      const width = Math.max(1, Math.floor(canvas.clientWidth * ratio));
      const height = Math.max(1, Math.floor(canvas.clientHeight * ratio));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
      }
    }

    function render(now) {
      resize();
      gl.useProgram(program);
      gl.enableVertexAttribArray(position);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
      gl.uniform1f(timeUniform, now * 0.001);
      gl.uniform2f(resolutionUniform, canvas.width, canvas.height);
      gl.uniform2f(pointerUniform, pointer.x, pointer.y);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      animationFrame = window.requestAnimationFrame(render);
    }

    function onPointerMove(event) {
      pointer.x = event.clientX / Math.max(window.innerWidth, 1);
      pointer.y = 1 - event.clientY / Math.max(window.innerHeight, 1);
    }

    window.addEventListener('resize', resize, { passive: true });
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    animationFrame = window.requestAnimationFrame(render);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onPointerMove);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
      gl.deleteShader(vertexShader);
      gl.deleteShader(fragmentShader);
    };
  }, []);

  return <canvas ref={canvasRef} className="shader-canvas" aria-hidden="true" />;
}

function SiteHeader({ mobileOpen, setMobileOpen, activeSection, onBooking }) {
  const navItems = [
    { id: 'experiencias', label: 'Experiencias' },
    { id: 'precios', label: 'Precios' },
    { id: 'cumples', label: 'Cumples' },
    { id: 'premios', label: 'Premios' },
    { id: 'contacto', label: 'Cómo llegar' },
  ];

  function navigate(id) {
    setMobileOpen(false);
    scrollToSection(id);
  }

  return (
    <header className="site-header">
      <Brand />
      <nav className={`site-nav ${mobileOpen ? 'site-nav--open' : ''}`} aria-label="Navegación principal">
        {navItems.map((item) => (
          <button key={item.id} type="button" className={activeSection === item.id ? 'is-active' : ''} onClick={() => navigate(item.id)}>
            {item.label}
          </button>
        ))}
        <button className="site-nav__mobile-cta" type="button" onClick={() => { setMobileOpen(false); onBooking(); }}>
          Reserva tu partida <ArrowRight size={16} />
        </button>
      </nav>
      <button className="header-cta" type="button" onClick={onBooking}>
        <span>Reserva tu partida</span>
        <ArrowRight size={17} />
      </button>
      <button className="menu-toggle" type="button" aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'} aria-expanded={mobileOpen} onClick={() => setMobileOpen((open) => !open)}>
        {mobileOpen ? <X size={22} /> : <Menu size={22} />}
      </button>
    </header>
  );
}

function SectionHeading({ kicker, title, description, align = 'left', action }) {
  return (
    <div className={`section-heading section-heading--${align}`}>
      <div>
        <span className="section-kicker">{kicker}</span>
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {action || null}
    </div>
  );
}

function Hero({ onBooking, onRoute }) {
  return (
    <section className="hero section-shell" id="inicio">
      <div className="hero__copy">
        <h1>Bolos, billar y <span>planazo</span> en Alcorcón</h1>
        <p className="hero__lead">Un sitio para venir a jugar, picarte, celebrar y quedarte un rato más. Elige tu experiencia y deja que ruede la bola.</p>
        <div className="hero__actions">
          <button className="button button--primary" type="button" onClick={onBooking}>Reserva tu partida <ArrowRight size={18} /></button>
          <button className="button button--ghost" type="button" onClick={onRoute}><Route size={17} /> Cómo llegar</button>
        </div>
        <div className="hero__meta" aria-label="Datos destacados">
          <span><Zap size={15} /> Desde 7,60 €</span>
          <span><Clock3 size={15} /> Abierto hasta las 02:00 los sábados</span>
        </div>
      </div>
      <div className="hero__media-wrap">
        <div className="hero__media-glow" aria-hidden="true" />
        <div className="hero__media">
          <img src="./venue/hero-bta.jpg" alt="Pistas de bowling de Bowling Tres Aguas" />
          <div className="hero__media-caption">
            <span className="live-dot" />
            <span><strong>El plan empieza aquí</strong><small>Ven a jugar en Tres Aguas</small></span>
            <ArrowUpRight size={19} />
          </div>
        </div>
        <div className="hero__orbit hero__orbit--one" aria-hidden="true" />
        <div className="hero__orbit hero__orbit--two" aria-hidden="true" />
      </div>
      <div className="hero__scroll-note"><span>Desliza para elegir plan</span><span className="hero__scroll-line" /></div>
    </section>
  );
}

function SignalStrip() {
  return (
    <section className="signal-strip" aria-label="Información rápida">
      <div><span className="signal-strip__number">01</span><span><strong>Combos claros</strong><small>partida · bebida · calzado</small></span></div>
      <div><span className="signal-strip__number">02</span><span><strong>Horarios amplios</strong><small>incluido sábado noche</small></span></div>
      <div><span className="signal-strip__number">03</span><span><strong>Plan para grupos</strong><small>cumples · amigos · empresa</small></span></div>
    </section>
  );
}

function ExperienceSection({ selectedId, onSelect, onBooking }) {
  const selected = EXPERIENCES.find((experience) => experience.id === selectedId) || EXPERIENCES[0];
  return (
    <section className="section-shell experience-section" id="experiencias">
      <SectionHeading kicker="ELIGE TU EXPERIENCIA" title="Una pista no es el único plan." description="Empieza con bolos, cambia a billar o alarga la tarde con una ronda de arcade. La web queda preparada para actualizar el catálogo real del local." />
      <div className="experience-rail" role="tablist" aria-label="Experiencias disponibles">
        {EXPERIENCES.map((experience) => (
          <button key={experience.id} type="button" role="tab" aria-selected={selectedId === experience.id} className={`experience-card experience-card--${experience.tone} ${selectedId === experience.id ? 'is-active' : ''}`} onClick={() => onSelect(experience.id)}>
            <img src={experience.image} alt={experience.alt} loading="lazy" />
            <span className="experience-card__shade" />
            <span className="experience-card__icon"><ExperienceIcon id={experience.id} /></span>
            <span className="experience-card__label">{experience.label}</span>
            <ArrowUpRight className="experience-card__arrow" size={18} />
          </button>
        ))}
      </div>
      <div className="experience-detail" aria-live="polite">
        <div className="experience-detail__index">0{EXPERIENCES.findIndex((experience) => experience.id === selected.id) + 1}</div>
        <div className="experience-detail__copy">
          <span className={`status status--${selected.tone}`}><span />{selected.status}</span>
          <h3>{selected.title}</h3>
          <p>{selected.description}</p>
        </div>
        <button className="button button--small button--ghost" type="button" onClick={() => onBooking(selected.label)}>Pedir disponibilidad <ArrowRight size={16} /></button>
      </div>
    </section>
  );
}

function ExperienceIcon({ id }) {
  if (id === 'billiards') return <span className="pool-icon">8</span>;
  if (id === 'arcade') return <Gamepad2 size={23} />;
  if (id === 'kids') return <UsersRound size={23} />;
  return <span className="pins-icon"><span /><span /><span /></span>;
}

function PricingSection({ selectedDay, onDayChange, onBooking }) {
  const pricing = useMemo(() => getPricingForDay(selectedDay), [selectedDay]);
  return (
    <section className="section-shell pricing-section" id="precios">
      <SectionHeading kicker="PRECIOS POR DÍA" title="Elige cuándo vienes. La tarifa te sigue." description="Tarifas publicadas por el local · IVA incluido · precio por persona." action={<a className="inline-link" href={VENUE.officialUrl} target="_blank" rel="noreferrer">Ver fuente oficial <ExternalLink size={15} /></a>} />
      <div className="day-tabs" role="tablist" aria-label="Tarifas por día de la semana">
        {PRICE_DAYS.map((day) => (
          <button key={day.id} type="button" role="tab" aria-selected={day.id === selectedDay} className={day.id === selectedDay ? 'is-active' : ''} onClick={() => onDayChange(day.id)}>{day.label}</button>
        ))}
      </div>
      <div className="pricing-context"><span>{pricing.name}</span><span>{pricing.note}</span></div>
      <div className="price-list">
        {pricing.combos.map((combo, index) => (
          <button key={combo.id} type="button" className={`price-row ${!combo.available ? 'is-disabled' : ''} price-row--${index + 1}`} onClick={() => combo.available && onBooking(combo.name)} disabled={!combo.available}>
            <span className="price-row__marker">0{index + 1}</span>
            <span className="price-row__copy"><strong>{combo.title} <em>+ {combo.description}</em></strong><small>{combo.available ? 'Disponible para este día' : 'No publicado para este día'}</small></span>
            <span className="price-row__price">{combo.price}</span>
            <ArrowRight className="price-row__arrow" size={18} />
          </button>
        ))}
      </div>
      <div className="pricing-footnote"><span><Check size={15} /> Las condiciones pueden cambiar en vísperas y festivos.</span><span><Phone size={14} /> Confirma disponibilidad: <a href={VENUE.phoneHref}>{VENUE.phone}</a></span></div>
    </section>
  );
}

function HoursPanel() {
  return (
    <div className="hours-panel">
      <div className="hours-panel__heading"><span className="icon-disc"><Clock3 size={18} /></span><span><strong>Horario de bolera</strong><small>Planifica tu llegada</small></span></div>
      <div className="hours-list">
        {HOURS.map((entry) => <div className="hours-row" key={entry.days}><span className={`hours-row__dot hours-row__dot--${entry.accent}`} /><span>{entry.days}</span><strong>{entry.hours}</strong></div>)}
      </div>
    </div>
  );
}

function EventSection({ onBooking }) {
  return (
    <section className="event-section section-shell" id="cumples">
      <div className="event-section__media"><img src="./venue/hero-birthday.jpg" alt="Celebración de cumpleaños en una bolera" loading="lazy" /><span className="event-section__badge"><PartyPopper size={17} /> Planazo en grupo</span></div>
      <div className="event-section__copy">
        <span className="section-kicker">CUMPLES · EMPRESA · GRUPOS</span>
        <h2>Que el plan se recuerde después del último bolo.</h2>
        <p>La web oficial anuncia bolo cumple y parque cumple. Cuéntanos la fecha, cuántos sois y qué queréis celebrar; el equipo del local confirma el protocolo y la disponibilidad.</p>
        <div className="event-section__actions"><button className="button button--coral" type="button" onClick={() => onBooking('Cumpleaños / evento')}>Pide info sin compromiso <ArrowRight size={18} /></button><a className="text-link" href={`mailto:${VENUE.email}`}>Escribir a la bolera <Mail size={15} /></a></div>
      </div>
    </section>
  );
}

function RewardsSection() {
  const [filter, setFilter] = useState('catalog');
  const filterOptions = [
    { id: 'catalog', label: 'Catálogo' },
    { id: 'games', label: '¿Qué da tickets?' },
  ];
  return (
    <section className="section-shell rewards-section" id="premios">
      <SectionHeading kicker="TICKETS Y PREMIOS" title="Deja sitio para el premio de la revancha." description="Módulo listo para el catálogo real del local. Hoy se muestra como borrador editable porque la web oficial no publica tickets ni premios confirmados." />
      <div className="reward-tabs" role="tablist" aria-label="Información de tickets y premios">
        {filterOptions.map((option) => <button key={option.id} type="button" role="tab" aria-selected={filter === option.id} className={filter === option.id ? 'is-active' : ''} onClick={() => setFilter(option.id)}>{option.label}</button>)}
      </div>
      {filter === 'catalog' ? <RewardCatalog /> : <TicketGames />}
    </section>
  );
}

function RewardCatalog() {
  return (
    <div className="reward-board">
      <div className="reward-board__hero"><div className="trophy-art"><Trophy size={58} strokeWidth={1.2} /></div><div><span className="status status--lime"><span />Borrador de catálogo</span><h3>Premios que molan</h3><p>Tramos orientativos para configurar cuando el local confirme stock, nombres y canje.</p></div></div>
      <div className="reward-grid">{REWARD_DRAFT.map((reward) => <article className={`reward-item reward-item--${reward.tone}`} key={reward.id}><span className="reward-item__icon"><Gift size={18} /></span><div><strong>{reward.name}</strong><small>{reward.detail}</small></div><span className="reward-item__tickets"><Ticket size={14} />{reward.tickets}</span></article>)}</div>
      <p className="module-note"><Sparkles size={15} /> Propuesta visual, no tarifa oficial. Se puede alimentar desde Cloudflare cuando el propietario entregue el catálogo definitivo.</p>
    </div>
  );
}

function TicketGames() {
  return (
    <div className="ticket-board">
      <div className="ticket-board__top"><span className="ticket-board__stamp"><Ticket size={18} /> Estado de información</span><p>Sin inventar: aquí cada juego queda marcado con lo que está publicado y lo que debe confirmar el local.</p></div>
      <div className="ticket-table" role="table" aria-label="Estado de tickets por experiencia">
        {TICKET_GAMES.map((game) => <div className="ticket-row" role="row" key={game.id}><span className={`ticket-row__icon ticket-row__icon--${game.tone}`}><ExperienceIcon id={game.name === 'Bolos' ? 'bowling' : game.name === 'Billar' ? 'billiards' : game.name === 'Arcade' ? 'arcade' : 'kids'} /></span><span className="ticket-row__name"><strong>{game.name}</strong><small>{game.detail}</small></span><span className={`ticket-answer ticket-answer--${game.answer === 'No consta' ? 'neutral' : 'pending'}`}>{game.answer}</span></div>)}
      </div>
    </div>
  );
}

function LocationSection({ onRoute }) {
  return (
    <section className="location-section section-shell" id="contacto">
      <div className="location-section__copy"><SectionHeading kicker="CÓMO LLEGAR" title="Nos vemos en Tres Aguas." description="Abre Google Maps y elige si vienes andando, en coche o en transporte público. La ruta se genera con el destino de la bolera." /><div className="address-line"><span className="address-line__icon"><MapPin size={20} /></span><div><strong>{VENUE.address}</strong><span>{VENUE.postalCode} · Alcorcón, Madrid</span></div></div><div className="location-actions"><button className="button button--primary button--small" type="button" onClick={onRoute}><Route size={16} /> Elegir ruta</button><a className="button button--ghost button--small" href={VENUE.mapsUrl} target="_blank" rel="noreferrer">Ver en Maps <ExternalLink size={15} /></a></div><div className="location-contact"><a href={VENUE.phoneHref}><Phone size={15} /> {VENUE.phone}</a><a href={`mailto:${VENUE.email}`}><Mail size={15} /> {VENUE.email}</a></div></div>
      <div className="map-visual" role="img" aria-label="Vista estilizada de la ubicación de Bowling Tres Aguas en Alcorcón"><svg className="map-visual__roads" viewBox="0 0 760 460" aria-hidden="true"><path d="M-20 98 C150 88 230 190 380 132 S610 64 790 110" /><path d="M40 390 C180 315 230 326 344 236 S564 200 790 286" /><path d="M250 -20 C292 94 278 176 342 238 S424 362 448 490" /><path d="M560 -20 C516 92 564 156 520 238 S560 360 650 486" /><path d="M-20 252 C130 230 180 250 300 228 S540 294 790 244" /></svg><div className="map-visual__district map-visual__district--one">Tres Aguas</div><div className="map-visual__district map-visual__district--two">Alcorcón Central</div><div className="map-pin"><span><MapPin size={25} fill="currentColor" /></span><strong>Tres Aguas</strong><small>Bowling Tres Aguas</small></div><div className="map-visual__legend"><span><span className="map-legend-dot" /> destino</span><span><span className="map-legend-line" /> ruta Google Maps</span></div></div>
    </section>
  );
}

function Footer({ onBooking }) {
  return (
    <footer className="site-footer section-shell"><div className="site-footer__main"><Brand footer /><div className="footer-links"><a href="#experiencias">Experiencias</a><a href="#precios">Precios</a><a href="#cumples">Cumples</a><a href="#premios">Premios</a></div><div className="footer-contact"><a href={VENUE.phoneHref}><Phone size={15} /> {VENUE.phone}</a><a href={`mailto:${VENUE.email}`}><Mail size={15} /> {VENUE.email}</a></div><div className="footer-socials"><a href="https://www.instagram.com/bowling3aguas/?hl=es" target="_blank" rel="noreferrer" aria-label="Instagram"><span>ig</span></a><a href="https://es-es.facebook.com/BowlingTA/" target="_blank" rel="noreferrer" aria-label="Facebook"><span>f</span></a></div></div><div className="site-footer__bottom"><span>© {new Date().getFullYear()} Bowling Tres Aguas</span><span>Información y tarifas sujetas a cambios. Confirma condiciones en el local.</span><button type="button" onClick={onBooking}>Pedir disponibilidad <ArrowUpRight size={14} /></button></div></footer>
  );
}

function BookingDrawer({ open, booking, setBooking, onClose, onSubmit, submitted }) {
  if (!open) return null;
  return (
    <div className="overlay" role="presentation" onMouseDown={onClose}>
      <aside className="booking-drawer" role="dialog" aria-modal="true" aria-labelledby="booking-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="booking-drawer__head"><div><span className="section-kicker">SOLICITUD DE DISPONIBILIDAD</span><h2 id="booking-title">Monta tu plan.</h2></div><button className="close-button" type="button" aria-label="Cerrar solicitud" onClick={onClose}><X size={21} /></button></div>
        {submitted ? <BookingSuccess booking={booking} onClose={onClose} /> : <form className="booking-form" onSubmit={onSubmit}><p>Rellena lo básico y te dejamos la solicitud preparada para enviarla directamente a <strong>{VENUE.email}</strong>. La confirmación final la hace el equipo del local.</p><div className="form-grid"><label><span>Tu nombre</span><input required value={booking.name} onChange={(event) => setBooking({ ...booking, name: event.target.value })} placeholder="Ej. María" /></label><label><span>Teléfono o email</span><input required value={booking.contact} onChange={(event) => setBooking({ ...booking, contact: event.target.value })} placeholder="¿Cómo te contactamos?" /></label><label><span>Fecha preferida</span><input type="date" value={booking.date} onChange={(event) => setBooking({ ...booking, date: event.target.value })} /></label><label><span>Personas</span><select value={booking.groupSize} onChange={(event) => setBooking({ ...booking, groupSize: event.target.value })}>{['2', '3', '4', '5', '6', '7', '8', '9+'].map((size) => <option key={size} value={size}>{size} personas</option>)}</select></label></div><label><span>Qué os apetece</span><select value={booking.experience} onChange={(event) => setBooking({ ...booking, experience: event.target.value })}><option>Bolos</option><option>Bolos · Combo 1</option><option>Bolos · Combo 2</option><option>Bolos · Combo 3</option><option>Billar</option><option>Arcade</option><option>Zona kids</option><option>Cumpleaños / evento</option></select></label><label><span>Notas</span><textarea rows="3" value={booking.notes} onChange={(event) => setBooking({ ...booking, notes: event.target.value })} placeholder="Turno aproximado, cumpleaños, empresa…" /></label><button className="button button--primary booking-submit" type="submit"><Send size={17} /> Preparar solicitud <ArrowRight size={17} /></button><small className="form-note"><Check size={14} /> No se realiza ningún pago ni se confirma una reserva desde esta web.</small></form>}
      </aside>
    </div>
  );
}

function BookingSuccess({ booking, onClose }) {
  const mailto = formatBookingMailto(booking);
  return <div className="booking-success"><span className="booking-success__icon"><Check size={28} /></span><h3>Solicitud lista para enviar.</h3><p>Hemos preparado tu mensaje para {VENUE.email}. Solo falta abrir tu correo y pulsar enviar.</p><div className="booking-success__summary"><span>{booking.experience}</span><strong>{booking.groupSize} personas{booking.date ? ` · ${booking.date}` : ''}</strong></div><a className="button button--primary" href={mailto}>Abrir correo <Mail size={16} /></a><a className="button button--ghost" href={VENUE.phoneHref}><Phone size={16} /> Llamar a la bolera</a><button className="text-link text-link--center" type="button" onClick={onClose}>Volver a la web</button></div>;
}

function RouteModal({ open, onClose, copied, onCopy }) {
  if (!open) return null;
  return <div className="overlay overlay--route" role="presentation" onMouseDown={onClose}><div className="route-modal" role="dialog" aria-modal="true" aria-labelledby="route-title" onMouseDown={(event) => event.stopPropagation()}><div className="route-modal__head"><div><span className="section-kicker">DESTINO</span><h2 id="route-title">Cómo llegar a Tres Aguas</h2><p>{VENUE.address} · {VENUE.postalCode} {VENUE.city}</p></div><button className="close-button" type="button" aria-label="Cerrar rutas" onClick={onClose}><X size={21} /></button></div><div className="route-options">{ROUTE_MODES.map(({ id, label, detail, Icon }) => <a key={id} className="route-option" href={getDirectionsUrl(id)} target="_blank" rel="noreferrer"><span className="route-option__icon"><Icon size={21} /></span><span><strong>{label}</strong><small>{detail}</small></span><ExternalLink size={16} /></a>)}</div><div className="route-modal__footer"><a href={VENUE.mapsUrl} target="_blank" rel="noreferrer">Abrir búsqueda en Google Maps <ExternalLink size={15} /></a><button type="button" onClick={onCopy}><Copy size={15} />{copied ? 'Enlace copiado' : 'Copiar dirección'}</button></div></div></div>;
}

function App() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedExperience, setSelectedExperience] = useState('bowling');
  const [selectedDay, setSelectedDay] = useState('martes');
  const [bookingOpen, setBookingOpen] = useState(false);
  const [booking, setBooking] = useState(INITIAL_BOOKING);
  const [submitted, setSubmitted] = useState(false);
  const [routeOpen, setRouteOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeSection, setActiveSection] = useState('inicio');

  useEffect(() => {
    const ids = ['inicio', 'experiencias', 'precios', 'cumples', 'premios', 'contacto'];
    const sections = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (!window.IntersectionObserver) return undefined;
    const observer = new window.IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible?.target?.id) setActiveSection(visible.target.id);
    }, { rootMargin: '-25% 0px -55% 0px', threshold: [0.1, 0.4, 0.7] });
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  function openBooking(experience = '') {
    setBooking((current) => ({ ...current, experience: experience || current.experience }));
    setSubmitted(false);
    setBookingOpen(true);
  }

  function closeBooking() {
    setBookingOpen(false);
    setSubmitted(false);
  }

  function submitBooking(event) {
    event.preventDefault();
    setSubmitted(true);
  }

  async function copyAddress() {
    const address = `${VENUE.name}, ${VENUE.address}, ${VENUE.postalCode} ${VENUE.city}`;
    try {
      if (!navigator.clipboard) return;
      await navigator.clipboard.writeText(address);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="app">
      <ShaderCanvas />
      <div className="page-noise" aria-hidden="true" />
      <SiteHeader mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} activeSection={activeSection} onBooking={() => openBooking()} />
      <main>
        <Hero onBooking={() => openBooking()} onRoute={() => setRouteOpen(true)} />
        <SignalStrip />
        <ExperienceSection selectedId={selectedExperience} onSelect={setSelectedExperience} onBooking={openBooking} />
        <PricingSection selectedDay={selectedDay} onDayChange={setSelectedDay} onBooking={openBooking} />
        <EventSection onBooking={openBooking} />
        <RewardsSection />
        <div className="hours-shell section-shell"><HoursPanel /></div>
        <LocationSection onRoute={() => setRouteOpen(true)} />
      </main>
      <Footer onBooking={() => openBooking()} />
      <BookingDrawer open={bookingOpen} booking={booking} setBooking={setBooking} onClose={closeBooking} onSubmit={submitBooking} submitted={submitted} />
      <RouteModal open={routeOpen} onClose={() => setRouteOpen(false)} copied={copied} onCopy={copyAddress} />
    </div>
  );
}

export { App };
